import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Gift, CheckCircle2, Search, Loader2 } from "lucide-react";
import { API } from "../config";

/**
 * Renders a "Pick your £50 gift card" picker for an order that
 * qualified for the Brother promo. Lists 60+ UK retailer brands.
 *
 * Props:
 *   orderId: required
 *   initialChoice: object | null  (from order.gift_card_choice)
 *   onSaved(choice): optional
 */
export default function GiftCardPicker({ orderId, initialChoice, onSaved }) {
  const [catalog, setCatalog] = useState([]);
  const [chosen, setChosen] = useState(initialChoice || null);
  const [savingId, setSavingId] = useState(null);
  const [filter, setFilter] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/gift-cards/catalog`)
      .then((res) => setCatalog(res.data?.items || []))
      .catch(() => setError("Could not load gift cards. Please refresh."));
  }, []);

  const categories = useMemo(() => {
    const cats = ["All", ...new Set(catalog.map((c) => c.category))];
    return cats;
  }, [catalog]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return catalog.filter((c) => {
      if (activeCat !== "All" && c.category !== activeCat) return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [catalog, filter, activeCat]);

  const handlePick = async (card) => {
    setSavingId(card.id);
    setError("");
    try {
      const res = await axios.post(`${API}/orders/${orderId}/gift-card-choice`, {
        card_id: card.id,
      });
      setChosen(res.data.choice);
      onSaved && onSaved(res.data.choice);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save your choice. Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  if (chosen) {
    return (
      <div
        className="rounded-2xl shadow-lg p-6 text-white text-center relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #16a34a 0%, #00a99d 100%)" }}
        data-testid="gift-card-confirmed"
      >
        <CheckCircle2 className="w-12 h-12 mx-auto mb-3" />
        <p className="text-sm uppercase tracking-wider font-bold opacity-90">Gift card chosen!</p>
        <h3 className="text-2xl md:text-3xl font-extrabold mt-1 mb-3">
          £50 {chosen.name} Gift Card on its way 🎁
        </h3>
        <p className="text-sm opacity-95 max-w-md mx-auto">
          We'll dispatch via Royal Mail Special Delivery within 24 hours of payment of invoice.
          Watch your inbox for the tracking number.
        </p>
        <button
          onClick={() => setChosen(null)}
          className="mt-4 text-xs underline opacity-80 hover:opacity-100"
          data-testid="gift-card-change"
        >
          Change my choice
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 p-6" data-testid="gift-card-picker">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0">
          <Gift className="w-6 h-6 text-amber-900" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider font-bold text-amber-700 mb-0.5">You qualified — pick yours!</p>
          <h3 className="text-xl md:text-2xl font-extrabold text-gray-900 leading-tight">
            Choose your FREE £50 Gift Card
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Pick from {catalog.length} popular UK retailers. Dispatched via Royal Mail Special Delivery
            within 24 hours of payment of invoice.
          </p>
        </div>
      </div>

      {/* Search + Category filter */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search brands…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#00a99d]"
            data-testid="gift-card-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCat(cat)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                activeCat === cat
                  ? "bg-gray-900 border-gray-900 text-white"
                  : "border-gray-300 text-gray-700 hover:border-gray-500"
              }`}
              data-testid={`gift-card-cat-${cat.replace(/\W+/g, "-").toLowerCase()}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3" data-testid="gift-card-picker-error">
          {error}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="text-center py-10 text-gray-500 text-sm">No brands match your search.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[480px] overflow-y-auto pr-1">
          {visible.map((card) => (
            <button
              key={card.id}
              onClick={() => handlePick(card)}
              disabled={savingId === card.id}
              className="group relative rounded-lg overflow-hidden shadow hover:shadow-xl hover:scale-[1.03] transition-all duration-150 disabled:opacity-50 text-left"
              data-testid={`gift-card-option-${card.id}`}
              style={{ background: card.color, color: card.text_color, aspectRatio: "1.6 / 1" }}
            >
              <div className="absolute inset-0 p-3 flex flex-col justify-between">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-bold text-xs leading-tight pr-1">{card.name}</div>
                  <div
                    className="w-6 h-6 rounded-full bg-white/95 flex items-center justify-center text-[9px] font-extrabold flex-shrink-0"
                    style={{ color: card.color }}
                  >
                    £50
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <img
                    src={`https://logo.clearbit.com/${card.domain}?size=64`}
                    alt=""
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="w-9 h-9 object-contain bg-white/95 rounded p-0.5"
                    loading="lazy"
                  />
                  <span className="text-[9px] uppercase tracking-wider opacity-80">{card.category}</span>
                </div>
              </div>
              {savingId === card.id && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
